import React, { useEffect, useRef, useState } from 'react';
import { 
  FaClipboardList, FaFileAlt, FaUpload, FaMoneyBillWave,
  FaCheckCircle, FaArrowRight, FaCalendarAlt, FaPhone,
  FaEnvelope, FaUserGraduate, FaBookOpen, FaRegClock,
  FaStar, FaDownload, FaInfoCircle, FaWhatsapp
} from 'react-icons/fa';
import { MdOutlineDocumentScanner, MdOutlinePayment } from 'react-icons/md';

const AdmissionProcessPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      id: 1,
      title: "Registration",
      icon: <FaClipboardList />,
      description: "Fill online registration form with basic details",
      details: "Visit our website and click on 'Apply Now'. Fill in your personal and academic details carefully.",
      duration: "5-10 minutes",
      documents: ["Personal Details", "Contact Information", "Course Selection"]
    },
    {
      id: 2,
      title: "Document Upload",
      icon: <FaUpload />,
      description: "Upload required documents online",
      details: "Upload scanned copies of all required documents in specified format (PDF/JPG).",
      duration: "10-15 minutes",
      documents: ["10th Marksheet", "12th Marksheet", "Photograph", "Signature"]
    },
    {
      id: 3,
      title: "Application Review",
      icon: <MdOutlineDocumentScanner />,
      description: "College reviews your application",
      details: "Our admission committee will verify your application and documents.",
      duration: "2-3 days",
      documents: ["Verification Process", "Eligibility Check", "Merit List Preparation"]
    },
    {
      id: 4,
      title: "Fee Payment",
      icon: <FaMoneyBillWave />,
      description: "Pay admission fees online",
      details: "Selected candidates can pay fees online through various payment methods.",
      duration: "15-20 minutes",
      documents: ["Online Banking", "Credit/Debit Card", "UPI Payment"]
    },
    {
      id: 5,
      title: "Confirmation",
      icon: <FaCheckCircle />,
      description: "Admission Confirmed!",
      details: "Receive confirmation email/SMS with enrollment details.",
      duration: "Instant",
      documents: ["Enrollment Number", "Student ID Card", "Welcome Kit"]
    }
  ];

  const documents = [
    { name: "10th Marksheet", required: true },
    { name: "12th Marksheet", required: true },
    { name: "Transfer Certificate", required: true },
    { name: "Character Certificate", required: true },
    { name: "Passport Size Photographs", required: true },
    { name: "Aadhar Card Copy", required: true },
    { name: "Caste Certificate (if applicable)", required: false },
    { name: "Income Certificate (for scholarship)", required: false }
  ];

  const importantDates = [
    { event: "Application Start Date", date: "15 March 2024", status: "active" },
    { event: "Last Date to Apply", date: "30 June 2024", status: "upcoming" },
    { event: "Merit List Release", date: "15 July 2024", status: "upcoming" },
    { event: "Document Verification", date: "20-25 July 2024", status: "upcoming" },
    { event: "Classes Commence", date: "1 August 2024", status: "upcoming" }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <FaUserGraduate className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent">
                Admission Process
              </span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4 max-w-2xl mx-auto">
              Simple and transparent admission process for academic year 2024-25
            </p>
          </div>

          {/* Steps Section */}
          <div className={`mb-12 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-10 left-0 right-0 h-1 bg-gray-200 hidden lg:block"></div>
              
              {/* Steps */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 relative">
                {steps.map((step, idx) => (
                  <div 
                    key={step.id}
                    className="relative cursor-pointer group"
                    onClick={() => setActiveStep(step.id)}
                  >
                    <div className="flex flex-col items-center">
                      {/* Step Circle */}
                      <div className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                        activeStep === step.id 
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg scale-110'
                          : 'bg-white border-2 border-blue-300 text-blue-500 group-hover:border-blue-500'
                      }`}>
                        <span className="text-2xl">{step.icon}</span>
                      </div>
                      {/* Step Number */}
                      <div className={`text-xs font-bold mt-2 ${
                        activeStep === step.id ? 'text-blue-600' : 'text-gray-400'
                      }`}>
                        Step {step.id}
                      </div>
                      {/* Step Title */}
                      <div className={`text-sm font-semibold text-center mt-1 ${
                        activeStep === step.id ? 'text-gray-800' : 'text-gray-500'
                      }`}>
                        {step.title}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step Details Card */}
            <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white text-2xl shrink-0">
                  {steps[activeStep - 1].icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {steps[activeStep - 1].title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {steps[activeStep - 1].details}
                  </p>
                  <div className="flex items-center gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <FaRegClock className="text-blue-500 text-sm" />
                      <span className="text-gray-500 text-xs">Est. Time: {steps[activeStep - 1].duration}</span>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-blue-700 text-xs font-semibold mb-2">Required Information:</p>
                    <div className="flex flex-wrap gap-2">
                      {steps[activeStep - 1].documents.map((doc, i) => (
                        <span key={i} className="bg-white px-2 py-1 rounded text-xs text-gray-600">
                          {doc}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents and Dates Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            
            {/* Required Documents */}
            <div className={`bg-white rounded-2xl shadow-xl p-6 transition-all duration-500 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`} style={{ transitionDelay: '100ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MdOutlineDocumentScanner className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Required Documents</h3>
              </div>
              <div className="space-y-2">
                {documents.map((doc, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      {doc.required ? (
                        <FaCheckCircle className="text-green-500 text-xs" />
                      ) : (
                        <FaInfoCircle className="text-gray-400 text-xs" />
                      )}
                      <span className="text-gray-600 text-sm">{doc.name}</span>
                    </div>
                    {doc.required && (
                      <span className="text-red-500 text-xs bg-red-50 px-2 py-0.5 rounded">Required</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <p className="text-yellow-700 text-xs">Note: All documents should be self-attested</p>
              </div>
            </div>

            {/* Important Dates */}
            <div className={`bg-white rounded-2xl shadow-xl p-6 transition-all duration-500 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaCalendarAlt className="text-blue-600 text-xl" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Important Dates</h3>
              </div>
              <div className="space-y-3">
                {importantDates.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">{item.event}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-800 text-sm font-semibold">{item.date}</span>
                      {item.status === 'active' && (
                        <span className="bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded">Active</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-blue-700 text-xs">⚠️ Dates are subject to change. Check website for updates.</p>
              </div>
            </div>
          </div>

          {/* How to Apply Section */}
          <div className={`bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '300ms' }}>
            <div className="text-center text-white mb-6">
              <h3 className="text-2xl font-bold">How to Apply?</h3>
              <p className="text-blue-100 text-sm mt-2">Follow these simple steps to complete your admission</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-xl font-bold">1</span>
                </div>
                <p className="text-white font-semibold text-sm">Online Registration</p>
                <p className="text-blue-100 text-xs mt-1">Fill basic details</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-xl font-bold">2</span>
                </div>
                <p className="text-white font-semibold text-sm">Upload Documents</p>
                <p className="text-blue-100 text-xs mt-1">Submit required documents</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-xl font-bold">3</span>
                </div>
                <p className="text-white font-semibold text-sm">Pay Application Fee</p>
                <p className="text-blue-100 text-xs mt-1">Complete payment process</p>
              </div>
            </div>
          </div>

          {/* Contact for Help */}
          <div className={`bg-white rounded-2xl shadow-xl p-6 mb-10 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '400ms' }}>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <FaWhatsapp className="text-green-500 text-xl" />
                </div>
                <div>
                  <p className="text-gray-800 font-semibold">Need Help with Admission?</p>
                  <p className="text-gray-500 text-sm">Contact our admission helpline</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-600 transition">
                  <FaWhatsapp />
                  WhatsApp
                </button>
                <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition">
                  <FaPhone />
                  Call Now
                </button>
                <button className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-200 transition">
                  <FaDownload />
                  Brochure
                </button>
              </div>
            </div>
          </div>

          {/* Apply Now Button */}
          <div className={`text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`} style={{ transitionDelay: '500ms' }}>
            <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
              Apply Now for 2024-25
              <FaArrowRight className="text-sm" />
            </button>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default AdmissionProcessPage;